import { useState, useRef, useEffect, Dispatch, SetStateAction } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import styles from './styles.module.css'
import PrimaryButton from '../../../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../../../global/components/buttons/secondary-button/SecondaryButton'
import { CustomerInformation } from '../customer-information/CustomerInformation'
import { AppointmentSlots } from '../appointment-slot/AppointmentSlots'
import { PaymentInformation } from '../payment-information/PaymentInformation'
import { ConfirmAppointment } from '../confirm-appointment/ConfirmAppointment'
import { TireSizeQuantity } from '../choose-service/TireSizeQuantity'
import ServiceDropDown from '../../../../../global/components/service-drop-down/ServiceDropDown'
import { ISchedulerStateProps, getInitialStateProps } from './initialData'
import {
  MODES,
  SCHEDULING_KEYS,
  TITLES,
} from '../../../../../global/constants/scheduler'
import ServicePriceOverride from '../../../../../global/components/service-price-override/ServicePriceOverride'
import { IOrder } from '../../../../../../../app/entities/Order'
import removeFreeOrderReasonAndCalculateCosts from '../../../../../global/utils/remove-free-order-reason-and-calculate-costs'
import { schedulerFlow } from './scheduleRoutes'
import { Address } from '../../../../../../../app/types/scheduling'

interface IScheduleModalProps {
  isOpen: boolean
  handleClosed: Function
  chosenAddress: Address | null
}
export interface ITiresProps {
  frontCount: number
  rearCount: number
  frontTire: ITireProps
  rearTire: ITireProps
  objectId?: string | undefined
}
export interface ITireProps {
  aspectRatio: string
  width: string
  diameter: string
  objectId?: string | undefined
}
export interface ITimeSlot {
  type: string
  fromTime: number
  toTime: number
  available?: string | null
}
export interface IScheduleModalComponentProps {
  setDisabled: Function
  setSchedulerState: Dispatch<SetStateAction<ISchedulerStateProps>>
  schedulerState: ISchedulerStateProps
  mode: MODES
  isCardOnFileCallback?: Function
  toggleToastError?: Function
  rescheduleReason?: string
  setCurrentSchedulingPage?: Dispatch<SetStateAction<SCHEDULING_KEYS>>
  viewOnly?: boolean
  order?: IOrder
}

function getInitialSchedulerState(
  chosenAddress: Address | null,
): ISchedulerStateProps {
  const initialState = getInitialStateProps()

  if (chosenAddress) {
    initialState.customerInformation.address = chosenAddress.address
    initialState.customerInformation.city = chosenAddress.city
    initialState.customerInformation.state = chosenAddress.state
    initialState.customerInformation.zipCode = chosenAddress.zipCode
  }

  return initialState
}

export default function ScheduleModal(props: IScheduleModalProps) {
  const { handleClosed, isOpen, chosenAddress } = props
  const ref = useRef<any>(null)

  const [currentSchedulingPage, setCurrentSchedulingPage] =
    useState<SCHEDULING_KEYS>(SCHEDULING_KEYS.customerInformation)
  const [nextDisabled, setNextDisabled] = useState(true)
  const [schedulerState, setSchedulerState] = useState<ISchedulerStateProps>(
    getInitialSchedulerState(chosenAddress),
  )
  const [priceOverrideIsDisabled, setPriceOverrideIsDisabled] = useState(true)

  function onScheduleModalClose() {
    handleClosed()
    setSchedulerState(getInitialStateProps())
    setCurrentSchedulingPage(SCHEDULING_KEYS.customerInformation)
    setNextDisabled(true)
    setPriceOverrideIsDisabled(true)
  }

  function forwardClick() {
    if (ref.current !== null) {
      ref.current.handleNextClick((successCallback: boolean) => {
        if (successCallback) {
          if (currentSchedulingPage === SCHEDULING_KEYS.confirmAppointment) {
            //the "confirm" action will on top of performing the ref handler
            //will also close the modal and reset the states
            onScheduleModalClose()
          } else {
            //the "next" action will on top of performing the ref handler
            //will also move us to the next scheduling page according
            //to the scheduler flow
            const nextSchedulingPage = schedulerFlow[currentSchedulingPage]
            setCurrentSchedulingPage(nextSchedulingPage)
          }
        }
      })
    }
  }

  function backClick() {
    const reversedFlow = Object.fromEntries(
      Object.entries(schedulerFlow).map(([key, value]) => [value, key]),
    )
    const nextSchedulingPage = reversedFlow[currentSchedulingPage]
    setCurrentSchedulingPage(nextSchedulingPage as SCHEDULING_KEYS)
  }

  useEffect(() => {
    setSchedulerState(getInitialSchedulerState(chosenAddress))
  }, [chosenAddress])

  useEffect(() => {
    const orderExistsAndIsFree =
      schedulerState.orderId && !schedulerState.freeOrderReason.isFree
    if (orderExistsAndIsFree) {
      removeFreeOrderReasonAndCalculateCosts(schedulerState, setSchedulerState)
    }
  }, [schedulerState.freeOrderReason.isFree])

  function renderComponent() {
    switch (currentSchedulingPage) {
      case SCHEDULING_KEYS.chooseAService:
        return (
          <TireSizeQuantity
            schedulerState={schedulerState}
            setSchedulerState={setSchedulerState}
            ref={ref}
            setDisabled={setNextDisabled}
            mode={MODES.SCHEDULER}
          />
        )
      case SCHEDULING_KEYS.appointmentSlot:
        return (
          <AppointmentSlots
            schedulerState={schedulerState}
            ref={ref}
            setSchedulerState={setSchedulerState}
            setDisabled={setNextDisabled}
            mode={MODES.SCHEDULER}
          />
        )
      case SCHEDULING_KEYS.paymentInformation:
        return (
          <PaymentInformation
            schedulerState={schedulerState}
            ref={ref}
            setSchedulerState={setSchedulerState}
            setDisabled={setNextDisabled}
            mode={MODES.SCHEDULER}
          />
        )
      case SCHEDULING_KEYS.confirmAppointment:
        return (
          <ConfirmAppointment
            schedulerState={schedulerState}
            setSchedulerState={setSchedulerState}
            ref={ref}
            setCurrentSchedulingPage={setCurrentSchedulingPage}
            setDisabled={setNextDisabled}
            mode={MODES.SCHEDULER}
          />
        )
      case SCHEDULING_KEYS.customerInformation:
      default:
        return (
          <CustomerInformation
            schedulerState={schedulerState}
            setSchedulerState={setSchedulerState}
            ref={ref}
            setDisabled={setNextDisabled}
            mode={MODES.SCHEDULER}
          />
        )
    }
  }

  return (
    <div>
      <Modal
        open={isOpen}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box className={styles.scheduleModal}>
          <div className={`${styles.scheduleModalPageTitle} font--bold`}>
            {TITLES[currentSchedulingPage]}
            {currentSchedulingPage === SCHEDULING_KEYS.chooseAService && (
              <>
                <ServiceDropDown
                  mode={MODES.EDIT}
                  defaultValue={schedulerState.service}
                  onServiceChange={(newValue: string) => {
                    setSchedulerState((prevState) => ({
                      ...prevState,
                      service: newValue,
                    }))
                  }}
                />
                <ServicePriceOverride
                  schedulerState={schedulerState}
                  setSchedulerState={setSchedulerState}
                  priceOverrideIsDisabled={priceOverrideIsDisabled}
                  setPriceOverrideIsDisabled={setPriceOverrideIsDisabled}
                />
              </>
            )}
          </div>
          <div
            style={{
              maxHeight: 'calc(100% - 10rem)',
              width: '100%',
              overflowX: 'auto',
              overflowY: 'scroll',
            }}
          >
            {renderComponent()}
          </div>
          <div className={styles.buttonContainer}>
            <div className={styles.flexGroupButtons}>
              <SecondaryButton
                disabled={
                  currentSchedulingPage === SCHEDULING_KEYS.customerInformation
                }
                buttonName='Back'
                onClick={backClick}
              />
              <PrimaryButton
                buttonName='Cancel'
                onClick={onScheduleModalClose}
              />
            </div>
            <PrimaryButton
              disabled={nextDisabled}
              buttonName={
                currentSchedulingPage === SCHEDULING_KEYS.confirmAppointment
                  ? 'Confirm'
                  : 'Next'
              }
              onClick={forwardClick}
            />
          </div>
        </Box>
      </Modal>
    </div>
  )
}
